/**
 * The words on a row in a grid — shared by five screens, so they live here rather than in any of them.
 *
 * Small in count and load-bearing out of proportion to it: `LIVE` and `Upcoming` are the corner of a
 * thumbnail, and `Watched`/`Resume` are the one word 4b's stored playhead gets to say out loud (the bar
 * itself is `aria-hidden` decoration, so the marker is the whole of what a screen reader hears).
 */
export const listing = {
  verified: 'Verified',
  /** A "- Topic" channel: YouTube made it, nobody uploads to it. */
  autoGeneratedTitle:
    "YouTube generated this channel from an artist's catalogue. Nobody uploads to it.",
  /** The same channel's visible badge, two words wide because it sits under a name in a card. */
  autoGenerated: 'Auto-generated',

  badge: {
    live: 'LIVE',
    upcoming: 'Upcoming',
  },

  /**
   * **The figures a card prints beside its title**, and the reason they are here at all: all three were
   * assembled in the JSX as `{item.viewCountText} views`, which is English word order nailed to a view.
   * A catalogue cannot translate a sentence that never reaches it, and these are the most-rendered words
   * in the app — every card in every grid on every screen.
   *
   * The count arrives already compacted (`Formatters.count`), so these take a string: the *number* was
   * pluralised where it still was one, which is `countedNoun`'s whole reason for existing.
   */
  meta: {
    views: (count: string): string => `${count} views`,
    subscribers: (count: string): string => `${count} subscribers`,
    videos: (count: string): string => `${count} videos`,
  },

  /**
   * The playlist card's own tag. Stated in the text as well as in the `aria-hidden` badge over the
   * thumbnail, so it carries the count in one of its two forms.
   */
  playlistTag: 'Playlist',
  playlistTagWithCount: (count: string): string => `Playlist · ${count} videos`,
  /** The grid search control (`listing/GridSearch`) — display-only, so the words say "showing". */
  search: {
    label: 'Search this list',
    activeTitle: (q: string): string => `Showing only entries matching “${q}”`,
    placeholder: 'Search by title',
    clear: 'Clear the search',
    submit: 'Done searching',
    /** Distinct from each screen's empty state: an emptied grid otherwise reads as an empty page. */
    noMatches: (q: string): string => `Nothing here matches “${q}”.`,
  },
  /**
   * The hover preview's corner note — two words at most, because it sits on a card's
   * thumbnail beside the duration badge. "No preview" is the honest reading of a refused track: an
   * instance that will not serve the storyboard and a video that never had one look the same from
   * here, and neither is something the reader can act on.
   */
  preview: {
    loading: 'Preview…',
    failed: 'No preview',
  },
  progress: {
    watched: 'Watched',
    /** Actionable rather than descriptive: clicking the row continues from the stored position. */
    resume: 'Resume',
    complete: 'You watched this to the end.',
    /** Watched on the strength of the account's history alone: nothing is stored about where. */
    inHistory: 'This video is in your watch history.',
    stoppedAt: (position: string): string =>
      `You stopped at ${position}. Opening it continues from there.`,
    stoppedAtOf: (position: string, duration: string): string =>
      `You stopped at ${position} of ${duration}. Opening it continues from there.`,
  },
} as const;
